import { atomWithDebounce, matches } from '@rematter/ui-web';
import { atom, getDefaultStore } from 'jotai';
import keyBy from 'lodash/keyBy';
import { nanoid } from 'nanoid';

import { IConversation, presetFilters, IContact } from './_types';
import * as chatState from './chat.state';

const MAX_CONVERSATIONS_COUNT = 100;
const PAGE_SIZE = 20;

export const $recentlyCreatedConversationId = atom<string | undefined>(
  undefined,
);

export const $selectedConversationId = atom<string | undefined>(undefined);

export const $selectedConversation = atom(
  (get) => {
    const allConversations = get($allConversations);
    const selectedConversationId = get($selectedConversationId);

    return allConversations.find((it) => it.id === selectedConversationId);
  },
  (_get, set, conversation: IConversation | undefined) => {
    set($selectedConversationId, conversation?.id);
  },
);
export const $searchTerm = atomWithDebounce('', 300);
export const $presetFilter = atom<(typeof presetFilters)[number]>('all');

export const $allConversations = atom<IConversation[]>([]);
export const $isLoadingConversations = atom(true);

$allConversations.onMount = () => {
  let participantSubscriptions: { unsubscribe: () => void }[] = [];
  let conversationsSubscription:
    | { unsubscribe: () => void; loadMore: (count: number) => void }
    | undefined;

  const store = getDefaultStore();

  const setup = () => {
    const session = store.get(chatState.$talkSession);

    if (!session) {
      return;
    }

    conversationsSubscription?.unsubscribe();
    participantSubscriptions.forEach((s) => s.unsubscribe());
    participantSubscriptions = [];

    const fetchParticipants = async (conversations: IConversation[]) => {
      participantSubscriptions.forEach((subscription) => {
        subscription.unsubscribe();
      });

      participantSubscriptions = [];

      conversations.forEach((conversation) => {
        const conversationRef = session.conversation(conversation.id);
        const subscription = conversationRef.subscribeParticipants(
          (participants) => {
            const conversations = store.get($allConversations);

            const conversationsWithParticipants = conversations.map((convo) =>
              convo.id === conversation.id
                ? {
                    ...convo,
                    participants: (participants ?? []) as unknown as IContact[],
                    custom: {
                      ...convo.custom,
                      conversationType: participants?.some(
                        (p) => p.user?.custom.contactType === 'external',
                      )
                        ? ('external' as const)
                        : ('internal' as const),
                    },
                  }
                : convo,
            );

            store.set($allConversations, conversationsWithParticipants);
          },
        );

        participantSubscriptions.push(subscription);
      });
    };

    conversationsSubscription = session.subscribeConversations(
      (rawConversations) => {
        const conversations = rawConversations
          .filter((it) => !it.custom.isDeleted)
          .map(
            (it) =>
              ({
                ...it,
                custom: {
                  ...it.custom,
                  objectsData: it.custom.objectsData
                    ? JSON.parse(it.custom.objectsData)
                    : [],
                },
              }) as IConversation,
          );

        const currentConversations = store.get($allConversations);
        const currentConversationsById = keyBy(
          currentConversations,
          (it) => it.id,
        );

        store.set(
          $allConversations,
          conversations.map((it) => ({
            ...currentConversationsById[it.id],
            ...it,
          })),
        );
        store.set($isLoadingConversations, false);

        const newlyCreatedConversation = conversations.find(
          (it) => it.id === store.get($recentlyCreatedConversationId),
        );

        if (newlyCreatedConversation) {
          store.set($selectedConversation, newlyCreatedConversation);
          store.set($recentlyCreatedConversationId, undefined);
        }

        fetchParticipants(conversations);
      },
    );

    for (let i = 0; i < MAX_CONVERSATIONS_COUNT / PAGE_SIZE; i++) {
      conversationsSubscription.loadMore(PAGE_SIZE);
    }
  };

  setup();

  const unsubSession = store.sub(chatState.$talkSession, setup);

  return () => {
    unsubSession();
    conversationsSubscription?.unsubscribe();
    participantSubscriptions.forEach((s) => s.unsubscribe());
  };
};

export const $filteredConversations = atom((get) => {
  const searchTerm = get($searchTerm.debouncedValue);
  const presetFilter = get($presetFilter);
  const allConversations = get($allConversations);

  const matchesPresetFilter = (conversation: IConversation) => {
    switch (presetFilter) {
      case 'all':
        return true;
      case 'unread':
        return conversation.unreadMessageCount > 0;
      case 'internal':
        return conversation.custom.conversationType === 'internal';
      case 'external':
        return conversation.custom.conversationType === 'external';
      default:
        return false;
    }
  };

  return allConversations.filter((conversation) => {
    const searchableString = [
      conversation.subject,
      conversation.custom.conversationType,
      ...conversation.custom.objectsData.map((obj) => [
        obj.title,
        obj.subtitle,
      ]),
      ...(conversation.participants?.map((p) => p.user?.name) ?? []),
    ]
      .flat()
      .join(' ');

    return (
      matchesPresetFilter(conversation) && matches(searchTerm, searchableString)
    );
  });
});

export const $unreadMessagesCount = atom((get) => {
  const allConversations = get($allConversations);
  return allConversations.reduce(
    (acc, conversation) => acc + conversation.unreadMessageCount,
    0,
  );
});

export const $createConversation = atom(null, (get, set) => {
  const talkSession = get(chatState.$talkSession);

  if (!talkSession) {
    return;
  }

  const newId = nanoid();

  set($recentlyCreatedConversationId, newId);

  talkSession.conversation(newId).createIfNotExists();
});
